import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useRef, useState } from 'react';
import { LTTooltip } from '@lambdatestincprivate/lt-components';
import { truncateMiddle } from '../format';
import { StatusIcon } from '../icons/StatusIcon';
import { AcRow } from './AcRow';
import styles from './coverage.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Chevron = function Chevron(_ref) {
  var open = _ref.open;
  return /*#__PURE__*/_jsx("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    style: {
      transform: open ? 'rotate(90deg)' : undefined
    },
    children: /*#__PURE__*/_jsx("path", {
      d: "M6 3.5 11 8l-5 4.5",
      stroke: "currentColor",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  });
};

/** happy/positive → the Figma's green "Positive" pill; negative → red;
 * any other authored case_type renders verbatim on a neutral pill. */
function casePill(caseType) {
  if (!caseType) return null;
  var t = caseType.toLowerCase();
  if (t === 'happy' || t === 'positive') {
    return {
      text: 'Positive',
      cls: styles.casePositive
    };
  }
  if (t === 'negative') return {
    text: 'Negative',
    cls: styles.caseNegative
  };
  return {
    text: caseType.charAt(0).toUpperCase() + caseType.slice(1),
    cls: styles.caseOther
  };
}

/** Packs author no scenario-level verdict — this is a DISPLAY-ONLY roll
 * of the exercised ACs' execution facts (any failed → failed, all passed
 * → passed, else no-signal/neutral). Nothing is scored here. */
function scenarioExecution(acs) {
  if (acs.length > 0 && acs.some(function (a) {
    return a.execution === 'failed';
  })) {
    return 'failed';
  }
  if (acs.length > 0 && acs.every(function (a) {
    return a.execution === 'passed';
  })) {
    return 'passed';
  }
  return 'skipped';
}

/**
 * One scenario of an expanded use-case (Figma 30786-12370): collapsed =
 * white row; expanded = muted header + its exercised acceptance criteria
 * as cards. The scenario's `intent` doubles as the display title — packs
 * author no separate scenario title/description.
 */
export function ScenarioCard(_ref2) {
  var _sc$intent;
  var sc = _ref2.sc,
    uc = _ref2.uc,
    aspect = _ref2.aspect,
    forceOpen = _ref2.forceOpen;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var rootRef = useRef(null);
  useEffect(function () {
    if (forceOpen === undefined) return undefined;
    setOpen(true);
    var raf = requestAnimationFrame(function () {
      var _rootRef$current;
      return (_rootRef$current = rootRef.current) === null || _rootRef$current === void 0 ? void 0 : _rootRef$current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    });
    return function () {
      return cancelAnimationFrame(raf);
    };
  }, [forceOpen]);
  var byId = new Map(uc.acceptanceCriteria.map(function (a) {
    return [a.id, a];
  }));
  var acs = sc.exercises.map(function (id) {
    return byId.get(id);
  }).filter(function (a) {
    return a !== undefined;
  });
  var pill = casePill(sc.caseType);
  return /*#__PURE__*/_jsxs("div", {
    ref: rootRef,
    className: styles.scCard,
    children: [/*#__PURE__*/_jsxs("button", {
      type: "button",
      className: "".concat(styles.scHead, " ").concat(open ? styles.scHeadOpen : ''),
      "aria-expanded": open,
      onClick: function onClick() {
        return setOpen(function (v) {
          return !v;
        });
      },
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.scChevron,
        children: /*#__PURE__*/_jsx(Chevron, {
          open: open
        })
      }), /*#__PURE__*/_jsxs("span", {
        className: styles.scMain,
        children: [/*#__PURE__*/_jsxs("span", {
          className: styles.scTitleRow,
          children: [/*#__PURE__*/_jsx(StatusIcon, {
            status: scenarioExecution(acs),
            size: 20
          }), /*#__PURE__*/_jsx("span", {
            className: styles.scTitle,
            children: (_sc$intent = sc.intent) !== null && _sc$intent !== void 0 ? _sc$intent : sc.id
          }), pill && /*#__PURE__*/_jsx("span", {
            className: "".concat(styles.casePill, " ").concat(pill.cls),
            children: pill.text
          })]
        }), /*#__PURE__*/_jsxs("span", {
          className: styles.scMetaRow,
          children: [/*#__PURE__*/_jsx(LTTooltip, {
            text: sc.id,
            direction: "bottom",
            children: /*#__PURE__*/_jsx("span", {
              className: styles.idChip,
              children: truncateMiddle(sc.id, 24)
            })
          }), /*#__PURE__*/_jsx("span", {
            className: styles.metaDot
          }), /*#__PURE__*/_jsxs("span", {
            children: ["Includes ", acs.length, " AC", acs.length === 1 ? '' : 's']
          })]
        })]
      })]
    }), open && /*#__PURE__*/_jsxs("div", {
      className: styles.scBody,
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.scAcHead,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.scAcTitle,
          children: "Acceptance Criteria"
        }), /*#__PURE__*/_jsx("span", {
          className: styles.counterBadge,
          children: acs.length
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: styles.scAcList,
        children: acs.map(function (ac) {
          return /*#__PURE__*/_jsx(AcRow, {
            ac: ac,
            score: aspect.acs[ac.id]
          }, ac.id);
        })
      })]
    })]
  });
}